import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { getCoupon } from "../../../apollo/server";
import { dealKind, dealNeedsCode, dealReward, dealThreshold } from "../../../utils/dealText";
import { clearPendingPromo, readPendingPromo, savePendingPromo } from "../../../utils/pendingPromo";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS, TYPE, VISUALLY_HIDDEN } from "../../../utils/designTokens";

const COUPON = gql`${getCoupon}`;

const money = (n) => `$${(Math.round(n * 100) / 100).toFixed(2)}`;
// Mid-sentence: "unlock free French Fries", not "unlock Free French Fries".
const lowerFirst = (s) => (s ? s[0].toLowerCase() + s.slice(1) : s);

/**
 * Progress toward the cart's spend-threshold deals. Before a threshold:
 * "Add $X more to unlock …" in gold. Past it: "… unlocked" in teal (a free
 * item) or green (money off), with the code to use when it needs one.
 */
export function DealProgress({ deals = [], subtotal = 0 }) {
  const withThreshold = deals
    .map((d) => ({ deal: d, at: dealThreshold(d) }))
    .filter((x) => x.at)
    .sort((a, b) => a.at - b.at);
  if (!withThreshold.length) return null;

  const unlocked = withThreshold.filter((x) => subtotal >= x.at);
  const next = withThreshold.find((x) => subtotal < x.at) || null;
  const best = unlocked.length ? unlocked[unlocked.length - 1] : null;

  const unlockedLine = (x) => {
    const freeItem = dealKind(x.deal) === "freeItem";
    const code = dealNeedsCode(x.deal) ? ` · use code ${x.deal.couponCode}` : "";
    return {
      text: `${dealReward(x.deal)} unlocked${code}`,
      color: freeItem ? C.teal : C.green,
      bg: freeItem ? C.tealSoft : C.greenSoft,
    };
  };

  return (
    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
      {best && (() => {
        const l = unlockedLine(best);
        return (
          <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25, borderRadius: RADIUS.control, bgcolor: l.bg, color: l.color }}>
            <CheckCircleRoundedIcon aria-hidden="true" sx={{ fontSize: 20, flexShrink: 0 }} />
            <Box sx={{ ...TYPE.meta, fontWeight: 700, color: l.color }}>{l.text}</Box>
          </Box>
        );
      })()}
      {next && (
        <Box sx={{ px: 1.5, py: 1.25, borderRadius: RADIUS.control, bgcolor: C.surface }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocalOfferOutlinedIcon aria-hidden="true" sx={{ fontSize: 18, color: C.gold, flexShrink: 0 }} />
            <Box sx={{ ...TYPE.meta, fontWeight: 700, color: C.gold }}>
              {`Add ${money(next.at - subtotal)} more to unlock ${lowerFirst(dealReward(next.deal))}`}
            </Box>
          </Box>
          <Box
            role="progressbar"
            aria-label={`Progress to ${dealReward(next.deal)}`}
            aria-valuemin={0}
            aria-valuemax={Math.round(next.at * 100) / 100}
            aria-valuenow={Math.round(Math.min(subtotal, next.at) * 100) / 100}
            sx={{ mt: 1, height: 8, borderRadius: RADIUS.pill, bgcolor: C.surface2, overflow: "hidden" }}
          >
            <Box sx={{ height: "100%", width: `${Math.max(4, Math.min(100, (subtotal / next.at) * 100))}%`, bgcolor: C.goldAccent, borderRadius: RADIUS.pill, transition: "width .3s ease" }} />
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** "Add something small": one-tap add pills, "+ Chips $2.00". */
export function CartUpsell({ items = [], onAdd, currencySymbol = "$" }) {
  if (!items.length || !onAdd) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ ...TYPE.meta, fontWeight: 700, color: C.ink, mb: 1 }}>Add something small</Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {items.map((food) => (
          <Box
            key={`${food.menuId}_${food._id}`}
            component="button"
            type="button"
            onClick={() => onAdd(food)}
            aria-label={`Add ${food.title}, ${currencySymbol}${food.variations[0].price.toFixed(2)}`}
            sx={{
              ...BUTTON_RESET,
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              height: 36,
              pl: 1,
              pr: 1.5,
              maxWidth: "100%",
              borderRadius: RADIUS.pill,
              border: `1px solid ${C.borderStrong}`,
              bgcolor: C.bg,
              fontFamily: FONT_BODY,
              fontSize: 13,
              fontWeight: 600,
              color: C.ink,
              "&::before": { content: '""', position: "absolute", inset: "-4px 0" },
              "&:hover": { bgcolor: C.surface, borderColor: C.ink },
              ...FOCUS_RING,
            }}
          >
            <AddRoundedIcon aria-hidden="true" sx={{ fontSize: 18, color: C.red }} />
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{food.title}</Box>
            <Box component="span" sx={{ color: C.muted, fontVariantNumeric: "tabular-nums" }}>
              {`${currencySymbol}${food.variations[0].price.toFixed(2)}`}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Promo code box. The code is checked against the server now and carried to
 * checkout, which applies it as if typed there (utils/pendingPromo.js).
 */
export function CartPromoCode({ restaurantId }) {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(() => readPendingPromo(restaurantId));
  const [error, setError] = useState("");
  useEffect(() => setSaved(readPendingPromo(restaurantId)), [restaurantId]);

  const [check, { loading }] = useMutation(COUPON, {
    onCompleted: ({ coupon }) => {
      if (!coupon || coupon.enabled === false) {
        setError("That code isn't available.");
        return;
      }
      savePendingPromo(coupon.title, restaurantId);
      setSaved(readPendingPromo(restaurantId));
      setText("");
      setError("");
    },
    onError: (e) => setError((e?.message || "").replace(/^GraphQL error:\s*/i, "").trim() || "That code isn't available."),
  });

  const submit = (e) => {
    e.preventDefault();
    const code = text.trim();
    if (!code || loading) return;
    setError("");
    check({ variables: { coupon: code, restaurantId: restaurantId || null } });
  };

  if (saved) {
    return (
      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, px: 1.5, minHeight: 44, borderRadius: RADIUS.control, bgcolor: C.greenSoft }}>
        <CheckCircleRoundedIcon aria-hidden="true" sx={{ fontSize: 20, color: C.green }} />
        <Box role="status" sx={{ ...TYPE.meta, flex: 1, color: C.green, fontWeight: 600 }}>
          <strong>{saved.code}</strong> will be applied at checkout
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => {
            clearPendingPromo();
            setSaved(null);
          }}
          sx={{ ...BUTTON_RESET, height: 44, px: 0.5, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: C.redLink, ...FOCUS_RING }}
        >
          Remove
        </Box>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={submit} sx={{ mt: 2 }} noValidate>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Box component="label" htmlFor="cart-promo-code" sx={VISUALLY_HIDDEN}>
          Promo code
        </Box>
        <Box
          component="input"
          id="cart-promo-code"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Promo code"
          autoComplete="off"
          autoCapitalize="characters"
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? "cart-promo-error" : undefined}
          sx={{
            flex: 1,
            minWidth: 0,
            height: 44,
            boxSizing: "border-box",
            px: 1.5,
            borderRadius: RADIUS.control,
            border: `1px solid ${error ? C.redLink : C.borderStrong}`,
            bgcolor: C.bg,
            fontFamily: FONT_BODY,
            fontSize: { xs: 16, md: 14 },
            color: C.ink,
            outline: "none",
            "&::placeholder": { color: C.muted, opacity: 1 },
            "&:focus": { borderColor: C.ink, boxShadow: `0 0 0 1px ${C.ink}` },
          }}
        />
        <Box
          component="button"
          type="submit"
          disabled={loading || !text.trim()}
          sx={{
            ...BUTTON_RESET,
            height: 44,
            px: 2,
            borderRadius: RADIUS.control,
            bgcolor: C.ink,
            color: "#FFFFFF",
            fontFamily: FONT_BODY,
            fontSize: 14,
            fontWeight: 700,
            "&:hover": { bgcolor: "#33312F" },
            "&:disabled": { bgcolor: C.disabledBg, color: C.disabledText, cursor: "default" },
            ...FOCUS_RING,
          }}
        >
          {loading ? "Checking" : "Apply"}
        </Box>
      </Box>
      {error && (
        <Box id="cart-promo-error" role="alert" sx={{ ...TYPE.meta, color: C.redLink, mt: 0.75 }}>
          {error}
        </Box>
      )}
    </Box>
  );
}
